import { motion } from "framer-motion";
import { Target, Info } from "lucide-react";
import { useState, useEffect, useContext, useRef } from "react";
import { getAccountabilityDashboard } from "../services/index";
import { useDistrict } from "../utils/DistrictContext";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext";
import InfoTooltip from "../components/ui/InfoTooltip";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";

export function AverageAccountability({ globalRange }) {
  // --- NEW: Time Range States ---
  const [selectedDays, setSelectedDays] = useState("Past Month");
  const timeOptions = [
    { label: "D", value: "Past Day" },
    { label: "W", value: "Past Week" },
    { label: "1M", value: "Past Month" },
    { label: "3M", value: "Past 3 Months" },
    { label: "6M", value: "Past 6 Months" },
    { label: "Y", value: "Past Year" },
  ];

  const [accountabilityData, setAccountabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedDistrict } = useDistrict();
  const buttonRef = useRef(null);
  const { theme, colors } = useTheme();
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  useEffect(() => {
    setSelectedDays(globalRange);
  }, [globalRange]);
  useEffect(() => {
    if (showInfoPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.top,
        left: rect.right + 10,
      });
    }
  }, [showInfoPopup]);

  useEffect(() => {
    const fetchAccountabilityData = async () => {
      try {
        setLoading(true);
        // NEW: Pass selectedDays to your API call so the backend knows what range to fetch
        const response = await getAccountabilityDashboard(selectedDays);
        const apiData = response?.data;

        if (apiData) {
          // 1. Define all possible metrics first
          const allMetrics = [
            {
              label: "Potable Plant",
              value:
                apiData.average_total !== undefined
                  ? `${apiData.average_total}%`
                  : "0%",
              numValue: apiData.average_total || 0,
              color: "#00b8f1",
            },
            {
              label: "Potable",
              value:
                apiData.average_potable !== undefined
                  ? `${apiData.average_potable}%`
                  : "0%",
              numValue: apiData.average_potable || 0,
              color: "#0080ff",
            },
            {
              label: "Non-Potable Plant",
              value:
                apiData.average_non_potable !== undefined
                  ? `${apiData.average_non_potable}%`
                  : "0%",
              numValue: apiData.average_non_potable || 0,
              // color: "#0066cc",
              color: "#A855F7",
            },
          ];

          // 2. Filter data based on district selection
          // If FB151, we only keep the "Plant" metric
          const finalData =
            selectedDistrict === "FB151"
              ? allMetrics
                  .filter((m) => m.label === "Potable Plant")
                  .map((m) => ({ ...m, label: "Total" }))
              : allMetrics;

          setAccountabilityData(finalData);
        }
      } catch (error) {
        console.error("Component Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccountabilityData();
  }, [selectedDistrict, selectedDays]); // NEW: Added selectedDays to dependency array

  const handleMetricClick = (label) => {
    const systemMap = {
      "Non-Potable Plant": "non_potable",
      Potable: "potable",
      "Potable Plant": "total",
    };

    navigate("/totalflow", {
      state: {
        tab: "flow",
        subTab: "accountability",
        system: systemMap[label],
        dateRange: selectedDays, // NEW: Pass the dynamic range instead of hardcoded "Past 30 Days"
      },
    });
  };

  return (
    <motion.div
      className="rounded-xl p-6 border-l-[0.5px] border-transparent transition-colors duration-300 relative overflow-hidden h-full flex flex-col group"
      initial={{ opacity: 0, scale: 0.85, rotateZ: 5 }}
      animate={{ opacity: 1, scale: 1, rotateZ: 0 }}
      transition={{ duration: 0.7, delay: 0.5, type: "spring", bounce: 0.35 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme === "white" ? colors.border : `${colors.primary}15`
        }`,
      }}
    >
      <div
        className="absolute top-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-400 ease-out rounded-full "
        style={{
          backgroundImage: colors.gradient,
        }}
      />

      <div className="relative z-10 h-[80%]">
        {/* NEW: Added wrapper to align Title on the left, buttons on the right */}
        <div className="flex items-center justify-between mb-6 w-full gap-2 max-[992px]:flex-wrap">
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundImage: colors.gradient }}
            >
              <Target className="w-5 h-5 text-white" />
            </div>
            <h3
              className="text-2xl font-bold tracking-tight"
              style={{ color: colors.text }}
            >
              Average Accountability
            </h3>
            {/* NEW: Dynamic tooltip text */}
            <InfoTooltip
              text={
                <div className="space-y-2 text-sm">
                  {/* <p>Average accountability for {selectedDays.toLowerCase()}</p> */}

                  <p className="font-semibold">
                    Average accountability per day over the selected period
                  </p>

                  <p>
                    <b>Accountability(%)</b> = (Demand ÷ Supply) * 100
                  </p>
                  {selectedDistrict !== "FB151" && (
                    <>
                      <div>
                        <p className="font-semibold mb-3">
                          Potable Accountability (%)
                        </p>
                        <p>Demand: Flow through Potable consumer meters</p>
                        <p>
                          Supply: Distribution flow - Interconnect - Non-potable
                          flow
                        </p>
                      </div>

                      <div>
                        <p className="font-semibold mb-3">
                          Potable Plant Accountability (%)
                        </p>
                        <p>
                          Demand: Potable consumer meters + Interconnect +
                          Non-potable flow
                        </p>
                        <p>Supply: Distribution meter flow</p>
                      </div>
                    </>
                  )}
                </div>
              }
            />
          </div>

          {/* NEW: Time Range Selector Buttons */}
          <div
            className="flex items-center p-1 rounded-lg border bg-opacity-20 shrink-0 cursor-pointer"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
            }}
          >
            {timeOptions.map((tp) => {
              const isActive = selectedDays === tp.value;
              return (
                <button
                  key={tp.value}
                  onClick={() => setSelectedDays(tp.value)}
                  className="px-2.5 py-1 rounded-md text-[10px] min-[1921px]:text-[12px] font-black transition-all duration-200 uppercase cursor-pointer"
                  style={{
                    backgroundColor: isActive ? colors.primary : "transparent",
                    color: isActive ? "#ffffff" : colors.textSecondary,
                    boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
                  }}
                >
                  {tp.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          style={{ height: "100%" }}
          className={`flex items-center justify-center mb-6 py-4`}
        >
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Spin size="large" />
              <span
                style={{
                  color: colors.textSecondary,
                  fontSize: "13px",
                }}
              >
                Loading accountability...
              </span>
            </div>
          ) : (
            <div
              className={`flex items-center max-[1650px]:flex-wrap ${
                accountabilityData.length === 1
                  ? "justify-center"
                  : "justify-around"
              } w-full`}
            >
              {accountabilityData.map((item, index) => (
                <div
                  key={item.label}
                  className="flex flex-col items-center gap-3"
                >
                  <div
                    className="relative w-28 h-28 cursor-pointer"
                    key={item.label}
                    onClick={() => handleMetricClick(item.label)}
                  >
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="56"
                        cy="56"
                        r="48"
                        stroke={colors.border}
                        strokeWidth="8"
                        fill="none"
                      />
                      <motion.circle
                        cx="56"
                        cy="56"
                        r="48"
                        stroke={item.color}
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        initial={{
                          strokeDasharray: "301.6 301.6",
                          strokeDashoffset: 301.6,
                        }}
                        animate={{
                          strokeDasharray: "301.6 301.6",
                          strokeDashoffset:
                            301.6 -
                            (301.6 * Math.min(item.numValue, 100)) / 100,
                        }}
                        transition={{
                          duration: 1.2,
                          delay: 0.5 + index * 0.15,
                          ease: "easeOut",
                        }}
                        style={{
                          filter: `drop-shadow(0 0 6px ${item.color}80)`,
                        }}
                      />
                    </svg>

                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.span
                        className="text-xl font-bold"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.8 + index * 0.15,
                          type: "spring",
                        }}
                        style={{ color: colors.text }}
                      >
                        {item.value}
                      </motion.span>
                    </div>
                  </div>

                  <span
                    className="text-xs text-center"
                    style={{ color: colors.textSecondary }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
